import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EventAssociation, EventAssociationInsert } from '@/types/events';
import { toast } from 'sonner';

const syncAssociationToContacts = async (
  brandId: string,
  association: { name: string; email?: string | null; phone?: string | null }
): Promise<string | null> => {
  if (!association.email) return null;
  
  // For organizations, use the full name as first_name
  const firstName = association.name;
  const lastName = '';
  
  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: association.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: association.phone || null,
      p_module: ['Events'],
      p_activity: ['association'],
      p_organization: association.name,
      p_title: null,
      p_user_id: null,
    });
    
    if (error) {
      console.error('Error syncing association to contacts:', error);
      return null;
    }
    
    return contactId;
  } catch (error) {
    console.error('Error syncing association to contacts:', error);
    return null;
  }
};

export function useEventsAssociations(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data: associations = [], isLoading } = useQuery({
    queryKey: ['events-associations', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data, error } = await supabase
        .from('os_events_associations')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');
      if (error) throw error;
      return data as EventAssociation[];
    },
    enabled: !!brandId,
  });

  const createAssociation = useMutation({
    mutationFn: async (payload: Omit<EventAssociationInsert, 'brand_id'>) => {
      if (!brandId) throw new Error('Brand required');
      
      // Sync to contacts first
      const contactId = await syncAssociationToContacts(brandId, {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
      });
      
      const { data, error } = await supabase
        .from('os_events_associations')
        .insert({ ...payload, brand_id: brandId, contact_id: contactId } as never)
        .select()
        .single();
      if (error) throw error;
      return data as EventAssociation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['events-associations', data.brand_id] });
      toast.success('Association created');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to create association'),
  });

  const updateAssociation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<EventAssociation> & { id: string }) => {
      // Get current association for brand_id and contact sync
      const { data: currentAssociation } = await supabase
        .from('os_events_associations')
        .select('brand_id, name, email, phone')
        .eq('id', id)
        .single();
      
      let contactId: string | null = null;
      if (currentAssociation) {
        contactId = await syncAssociationToContacts(currentAssociation.brand_id, {
          name: updates.name || currentAssociation.name,
          email: updates.email || currentAssociation.email,
          phone: updates.phone || currentAssociation.phone,
        });
      }
      
      const { data, error } = await supabase
        .from('os_events_associations')
        .update({ ...updates, ...(contactId ? { contact_id: contactId } : {}) })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as EventAssociation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['events-associations', data.brand_id] });
      toast.success('Association updated');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to update association'),
  });

  const deleteAssociation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('os_events_associations').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events-associations'] });
      toast.success('Association deleted');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to delete association'),
  });

  const importFromLms = useMutation({
    mutationFn: async (lmsAssociationIds: string[]) => {
      if (!brandId) throw new Error('Brand required');
      const { data: lmsRows, error: fetchError } = await supabase
        .from('os_lms_associations')
        .select('*')
        .in('id', lmsAssociationIds)
        .eq('brand_id', brandId);
      if (fetchError) throw fetchError;
      if (!lmsRows?.length) throw new Error('No LMS associations found');
      
      // Sync each association to contacts and build rows with contact_id
      const rows: (EventAssociationInsert & { contact_id?: string | null })[] = await Promise.all(
        lmsRows.map(async (row: Record<string, unknown>) => {
          const contactId = await syncAssociationToContacts(brandId, {
            name: row.name as string,
            email: row.email as string | null,
            phone: row.phone as string | null,
          });
          return {
            brand_id: brandId,
            name: row.name as string,
            description: (row.description as string) ?? null,
            logo_url: (row.logo_url as string) ?? null,
            academy_ids: Array.isArray(row.academy_ids) ? (row.academy_ids as string[]) : undefined,
            payment_type: (row.payment_type as 'commission' | 'static' | 'volume') ?? 'commission',
            commission_percentage: typeof row.commission_percentage === 'number' ? row.commission_percentage : 0,
            static_payment: typeof row.static_payment === 'number' ? row.static_payment : 0,
            payment_currency: (row.payment_currency as string) ?? 'EUR',
            xero_contact_id: (row.xero_contact_id as string) ?? null,
            email: (row.email as string) ?? null,
            phone: (row.phone as string) ?? null,
            address_street: (row.address_street as string) ?? null,
            address_city: (row.address_city as string) ?? null,
            address_region: (row.address_region as string) ?? null,
            address_postal_code: (row.address_postal_code as string) ?? null,
            address_country: (row.address_country as string) ?? null,
            tax_number: (row.tax_number as string) ?? null,
            company_number: (row.company_number as string) ?? null,
            bank_account_details: (row.bank_account_details as string) ?? null,
            contact_id: contactId,
          };
        })
      );
      
      const { data, error } = await supabase
        .from('os_events_associations')
        .insert(rows as never)
        .select();
      if (error) throw error;
      return data as EventAssociation[];
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['events-associations', brandId] });
      toast.success(`${data?.length ?? 0} association(s) imported from LMS`);
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Failed to import associations from LMS'),
  });

  return {
    associations,
    isLoading,
    createAssociation,
    updateAssociation,
    deleteAssociation,
    importFromLms,
  };
}
